import type { OrganizerMember } from '@pulsify/shared'
import { Check, Copy, QrCode, RefreshCw, Search, UserMinus, X } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import {
  useApproveAllMembers,
  useApproveMember,
  useDeclineMember,
  useOrganizerMembers,
  useRemoveMember,
  useRotateMemberInvite,
  useSetMembersEnabled,
} from '~/entities/channel/model/use-members'
import { offersApproveAll, shortLink } from '~/features/organizer/lib/member-list'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { initialsOf } from '~/shared/lib/initials'
import { copyToClipboard } from '~/shared/lib/share'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~/shared/ui/alert-dialog'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { QrDownload } from '~/shared/ui/qr-download'
import { Skeleton } from '~/shared/ui/skeleton'
import { Switch } from '~/shared/ui/switch'

type Target = { businessId: string; channelId: string }

function formatDay(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: 'medium' }).format(new Date(iso))
}

const failed = () => toast.error(m.membersActionFailed())

/** Memberships of an organizer page: the switch, the invite link, requests and members. */
export function MembersCard({
  businessId,
  channelId,
  name,
}: {
  businessId: string
  channelId: string
  name: string
}) {
  const target: Target = { businessId, channelId }
  const switchId = useId()
  const [query, setQuery] = useState('')
  const members = useOrganizerMembers(target, query.trim())
  const setEnabled = useSetMembersEnabled(target)
  const data = members.data

  if (!data) return members.isError ? null : <Skeleton className="h-48 w-full rounded-xl" />

  const toggle = (enabled: boolean) =>
    setEnabled.mutate(enabled, {
      onSuccess: () => toast.success(enabled ? m.membersEnabledToast() : m.membersDisabledToast()),
      onError: failed,
    })
  const showLists = data.membersEnabled || data.counts.active > 0 || data.counts.pending > 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.membersTitle()}</CardTitle>
        <CardDescription>{m.membersDescription()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor={switchId}>{m.membersEnable()}</Label>
            <p className="text-muted-foreground text-sm">{m.membersEnableDescription()}</p>
          </div>
          <Switch
            id={switchId}
            checked={data.membersEnabled}
            disabled={setEnabled.isPending}
            onCheckedChange={(checked) => toggle(checked === true)}
          />
        </div>

        {showLists ? (
          <>
            <p className="text-muted-foreground text-sm tabular-nums">
              {m.membersCounts({
                active: String(data.counts.active),
                pending: String(data.counts.pending),
              })}
            </p>
            {data.membersEnabled ? (
              <InviteLink target={target} url={data.inviteUrl} name={name} />
            ) : null}
            <Requests target={target} pending={data.pending} total={data.counts.pending} />
            <Members
              target={target}
              active={data.active}
              query={query}
              onQuery={setQuery}
              searching={members.isFetching}
            />
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

function InviteLink({ target, url, name }: { target: Target; url: string | null; name: string }) {
  const rotate = useRotateMemberInvite(target)
  const create = () => rotate.mutate(undefined, { onError: failed })
  const copy = async (link: string) => {
    if (await copyToClipboard(link)) toast.success(m.linkCopied())
    else toast.error(m.failedToCopyLink())
  }

  return (
    <section className="space-y-2">
      <div className="space-y-1">
        <h4 className="font-medium text-sm">{m.membersInviteTitle()}</h4>
        <p className="text-muted-foreground text-sm">{m.membersInviteDescription()}</p>
      </div>
      {url ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2">
          <p className="min-w-0 flex-1 truncate font-mono text-sm">{shortLink(url)}</p>
          <Button size="sm" onClick={() => copy(url)}>
            <Copy />
            {m.shareCopyShort()}
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={m.membersInviteQr()}
                title={m.membersInviteQr()}
              >
                <QrCode />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>{name}</DialogTitle>
                <DialogDescription className="break-all font-mono text-xs">{url}</DialogDescription>
              </DialogHeader>
              <QrDownload
                value={url}
                title={m.membersInviteQrAlt({ name })}
                fileName="pulsify-mitglied-werden"
                downloadLabel={m.membersInviteQrDownload()}
              />
            </DialogContent>
          </Dialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={rotate.isPending}
                aria-label={m.membersInviteRotate()}
                title={m.membersInviteRotate()}
              >
                <RefreshCw />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{m.membersInviteRotate()}</AlertDialogTitle>
                <AlertDialogDescription>{m.membersInviteRotateConfirm()}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
                <AlertDialogAction onClick={create}>{m.membersInviteRotate()}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <Button variant="secondary" disabled={rotate.isPending} onClick={create}>
          {m.membersInviteCreate()}
        </Button>
      )}
    </section>
  )
}

function Person({ member, detail }: { member: OrganizerMember; detail: string }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Avatar className="h-9 w-9">
        {member.avatarUrl ? (
          <img src={member.avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <AvatarFallback className="text-xs font-medium">
            {initialsOf(member.displayName)}
          </AvatarFallback>
        )}
      </Avatar>
      <div className="min-w-0">
        <p className="truncate font-medium text-sm">{member.displayName}</p>
        <p className="truncate text-muted-foreground text-xs">
          {member.username ? `@${member.username} · ` : ''}
          {detail}
        </p>
      </div>
    </div>
  )
}

function Requests({
  target,
  pending,
  total,
}: {
  target: Target
  pending: OrganizerMember[]
  total: number
}) {
  const approve = useApproveMember(target)
  const decline = useDeclineMember(target)
  const approveAll = useApproveAllMembers(target)
  const busy = approve.isPending || decline.isPending || approveAll.isPending

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium text-sm">{m.membersRequests()}</h4>
        {offersApproveAll(total) ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() =>
              approveAll.mutate(undefined, {
                onSuccess: (count) => toast.success(m.membersApprovedAll({ count: String(count) })),
                onError: failed,
              })
            }
          >
            <Check />
            {m.membersApproveAll()}
          </Button>
        ) : null}
      </div>
      {pending.length === 0 ? (
        <p className="text-muted-foreground text-sm">{m.membersRequestsEmpty()}</p>
      ) : (
        <ul className="divide-y">
          {pending.map((member) => (
            <li key={member.id} className="flex flex-wrap items-center gap-2 py-2">
              <Person
                member={member}
                detail={m.membersRequestedAt({ date: formatDay(member.requestedAt) })}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => approve.mutate(member.id, { onError: failed })}
                >
                  <Check />
                  {m.membersApprove()}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => decline.mutate(member.id, { onError: failed })}
                >
                  <X />
                  {m.membersDecline()}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Members({
  target,
  active,
  query,
  onQuery,
  searching,
}: {
  target: Target
  active: OrganizerMember[]
  query: string
  onQuery: (value: string) => void
  searching: boolean
}) {
  const remove = useRemoveMember(target)

  return (
    <section className="space-y-2">
      <h4 className="font-medium text-sm">{m.membersActive()}</h4>
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={query}
          placeholder={m.membersSearch()}
          aria-label={m.membersSearch()}
          className="pl-9"
          onChange={(event) => onQuery(event.target.value)}
        />
      </div>
      {active.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {searching ? m.loading() : m.membersActiveEmpty()}
        </p>
      ) : (
        <ul className="divide-y">
          {active.map((member) => (
            <li key={member.id} className="flex items-center gap-2 py-2">
              <Person
                member={member}
                detail={m.membersSince({ date: formatDay(member.since ?? member.requestedAt) })}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" disabled={remove.isPending}>
                    <UserMinus />
                    {m.membersRemove()}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {m.membersRemoveTitle({ name: member.displayName })}
                    </AlertDialogTitle>
                    <AlertDialogDescription>{m.membersRemoveDescription()}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => remove.mutate(member.id, { onError: failed })}
                    >
                      {m.membersRemove()}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
